import {
  Autocomplete,
  type AutocompleteRenderGetTagProps,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Controller, type Control } from "react-hook-form";
import {
  formatJapaneseDate,
  parseJapaneseDate,
} from "../../utils/japaneseDate";
import type {
  V2SearchArrayKey,
  V2SearchConditions,
  V2SearchFlagKey,
  V2SearchTextKey,
} from "../../utils/v2SearchParams";

type SearchControl = Control<V2SearchConditions>;

export interface ChoiceOption {
  value: string;
  label: string;
}

export const toOptions = (values: readonly string[]): ChoiceOption[] =>
  values.map((value) => ({ value, label: value }));

/** Autocomplete の選んだ値を Chip で出す。key は getTagProps から直接渡す（spread すると React が警告する） */
export const chipTags =
  (labelOf: (value: string) => string) =>
  (
    values: string[],
    getTagProps: AutocompleteRenderGetTagProps
  ): JSX.Element[] =>
    values.map((value, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      return (
        <Chip key={key} size="small" label={labelOf(value)} {...tagProps} />
      );
    });

const FieldLabel = ({
  label,
  htmlFor,
}: {
  label: string;
  htmlFor?: string;
}): JSX.Element => (
  <Typography
    component={htmlFor ? "label" : "div"}
    htmlFor={htmlFor}
    variant="body2"
    fontWeight="bold"
    sx={{ display: "block", mb: 0.5 }}
  >
    {label}
  </Typography>
);

export const TextConditionField = ({
  control,
  name,
  label,
  placeholder,
  helperText,
  disabled,
}: {
  control: SearchControl;
  name: V2SearchTextKey;
  label: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
}): JSX.Element => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <Box>
        <FieldLabel label={label} htmlFor={`v2-search-${name}`} />
        <TextField
          {...field}
          id={`v2-search-${name}`}
          size="small"
          fullWidth
          placeholder={placeholder}
          disabled={disabled}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? helperText}
        />
      </Box>
    )}
  />
);

/** 下限・上限の数値。全角数字・桁区切りのカンマも受け付ける（検証は v2SearchSchema） */
export const NumberRangeField = ({
  control,
  minName,
  maxName,
  label,
  unit,
  helperText,
  disabled,
}: {
  control: SearchControl;
  minName: V2SearchTextKey;
  maxName: V2SearchTextKey;
  label: string;
  unit?: string;
  helperText?: string;
  disabled?: boolean;
}): JSX.Element => {
  const input = (name: V2SearchTextKey, edge: string): JSX.Element => (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          size="small"
          disabled={disabled}
          inputProps={{
            inputMode: "decimal",
            "aria-label": `${label}の${edge}`,
          }}
          placeholder={edge}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message}
          sx={{ flex: 1, minWidth: 0 }}
        />
      )}
    />
  );
  return (
    <Box>
      <FieldLabel label={unit ? `${label}（${unit}）` : label} />
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {input(minName, "下限")}
        <Typography component="span" sx={{ pt: 1 }}>
          〜
        </Typography>
        {input(maxName, "上限")}
      </Stack>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
};

const dateHint = (value: string, edge: "start" | "end"): string | undefined => {
  if (value.trim() === "") return undefined;
  const iso = parseJapaneseDate(value, edge);
  if (iso == null) return undefined;
  const wareki = formatJapaneseDate(iso);
  const [y, m, d] = iso.split("-").map(Number);
  return `${y}年${m}月${d}日${wareki ? `（${wareki}）` : ""}`;
};

/**
 * 日付の範囲。西暦（2000/1/1）と和暦（平成12年1月1日・H12.1.1）を受け付け、
 * 読み取った日付を西暦・和暦で下に出す。年だけ・年月だけは範囲の端に合わせて補う
 */
export const DateRangeField = ({
  control,
  fromName,
  toName,
  label,
  helperText,
  disabled,
}: {
  control: SearchControl;
  fromName: V2SearchTextKey;
  toName: V2SearchTextKey;
  label: string;
  helperText?: string;
  disabled?: boolean;
}): JSX.Element => {
  const input = (
    name: V2SearchTextKey,
    edge: "start" | "end",
    placeholder: string
  ): JSX.Element => (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          size="small"
          disabled={disabled}
          placeholder={placeholder}
          inputProps={{
            "aria-label": `${label}の${edge === "start" ? "開始" : "終了"}`,
          }}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? dateHint(field.value, edge)}
          sx={{ flex: 1, minWidth: 0 }}
        />
      )}
    />
  );
  return (
    <Box>
      <FieldLabel label={label} />
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {input(fromName, "start", "例 H12.1.1")}
        <Typography component="span" sx={{ pt: 1 }}>
          〜
        </Typography>
        {input(toName, "end", "例 2010/12/31")}
      </Stack>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
};

/**
 * 複数選択。freeSolo なら選択肢にない語も入力できる（Enter で確定）
 */
export const ChoiceField = ({
  control,
  name,
  label,
  options,
  freeSolo = false,
  helperText,
  disabled,
}: {
  control: SearchControl;
  name: V2SearchArrayKey;
  label: string;
  options: ChoiceOption[];
  freeSolo?: boolean;
  helperText?: string;
  disabled?: boolean;
}): JSX.Element => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => {
      const labelOf = (value: string): string =>
        options.find((option) => option.value === value)?.label ?? value;
      return (
        <Box>
          <FieldLabel label={label} htmlFor={`v2-search-${name}`} />
          <Autocomplete<string, true, false, boolean>
            id={`v2-search-${name}`}
            multiple
            freeSolo={freeSolo}
            disabled={disabled}
            size="small"
            options={options.map((option) => option.value)}
            getOptionLabel={labelOf}
            value={field.value}
            onChange={(_, value) => {
              field.onChange(
                [...new Set(value.map((entry) => entry.trim()))].filter(Boolean)
              );
            }}
            renderTags={chipTags(labelOf)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  field.value.length > 0
                    ? undefined
                    : freeSolo
                    ? "選択または入力"
                    : "選択"
                }
                helperText={helperText}
              />
            )}
          />
        </Box>
      );
    }}
  />
);

/** 「指定なし／あり／なし」の3択 */
export const TriStateField = ({
  control,
  name,
  label,
  yesLabel = "あり",
  noLabel = "なし",
  helperText,
  disabled,
}: {
  control: SearchControl;
  name: V2SearchTextKey;
  label: string;
  yesLabel?: string;
  noLabel?: string;
  helperText?: string;
  disabled?: boolean;
}): JSX.Element => (
  <SelectField
    control={control}
    name={name}
    label={label}
    helperText={helperText}
    disabled={disabled}
    options={[
      { value: "yes", label: yesLabel },
      { value: "no", label: noLabel },
    ]}
  />
);

export const SelectField = ({
  control,
  name,
  label,
  options,
  emptyLabel = "指定なし",
  helperText,
  disabled,
}: {
  control: SearchControl;
  name: V2SearchTextKey;
  label: string;
  options: ChoiceOption[];
  emptyLabel?: string;
  helperText?: string;
  disabled?: boolean;
}): JSX.Element => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <Box>
        <FieldLabel label={label} htmlFor={`v2-search-${name}`} />
        <TextField
          {...field}
          id={`v2-search-${name}`}
          select
          size="small"
          fullWidth
          disabled={disabled}
          helperText={helperText}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">{emptyLabel}</MenuItem>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    )}
  />
);

export const CheckField = ({
  control,
  name,
  label,
  disabled,
}: {
  control: SearchControl;
  name: V2SearchFlagKey;
  label: string;
  disabled?: boolean;
}): JSX.Element => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <FormControlLabel
        disabled={disabled}
        control={
          <Checkbox
            size="small"
            checked={field.value}
            onChange={(event) => {
              field.onChange(event.target.checked);
            }}
          />
        }
        label={<Typography variant="body2">{label}</Typography>}
      />
    )}
  />
);

/** 少数の選択肢をボタンで複数選択する（種別など） */
export const ToggleChoiceField = ({
  control,
  name,
  label,
  options,
}: {
  control: SearchControl;
  name: V2SearchArrayKey;
  label: string;
  options: ChoiceOption[];
}): JSX.Element => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <Box>
        <FieldLabel label={label} />
        <ToggleButtonGroup
          size="small"
          color="primary"
          value={field.value}
          aria-label={label}
          onChange={(_, value: string[]) => {
            field.onChange(value);
          }}
        >
          {options.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              sx={{ px: 2, minHeight: 40 }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    )}
  />
);
